/**
 * Form utility functions for handling field updates and normalization
 */

/**
 * Compares a string field value with its original value to determine if it has changed.
 * Normalizes null and empty strings for comparison.
 * 
 * @param newValue - The new value from the form
 * @param originalValue - The original value from the entity
 * @param options - Configuration options
 * @returns The value to include in the update payload, or undefined if no change
 * 
 * @example
 * // For required fields (name)
 * const name = getChangedField(formData.name, user.name, { required: true });
 * 
 * @example
 * // For optional fields (phone, address)
 * const phone = getChangedField(formData.phone, user.phone);
 */
export function getChangedField(
  newValue: string | undefined | null,
  originalValue: string | undefined | null,
  options: { required?: boolean } = {}
): string | null | undefined {
  const { required = false } = options;
  
  // Normalize values (treat null and empty string as equivalent)
  const normalizedOriginal = originalValue || "";
  const normalizedNew = (newValue || "").trim();
  
  // For required fields, don't include if empty or unchanged
  if (required) {
    if (normalizedNew === "") return undefined;
    if (normalizedNew === normalizedOriginal) return undefined;
    return normalizedNew;
  }
  
  // For optional fields, allow clearing and changing
  if (normalizedNew === normalizedOriginal) return undefined;
  
  // Return the new value, or null if clearing (null is serialized in JSON to clear server-side)
  return normalizedNew || null;
}

/**
 * Builds an update payload by comparing form data with original entity data.
 * Only includes string fields that have actually changed.
 * 
 * @param formData - The form data from the user (only processes string fields)
 * @param originalData - The original entity data
 * @param fieldConfigs - Configuration for each field (which are required)
 * @returns An object containing only the changed fields
 * 
 * @example
 * const updateData = buildUpdatePayload(
 *   { name: formData.name, phone: formData.phone },
 *   user,
 *   { name: { required: true }, phone: {} }
 * );
 */
export function buildUpdatePayload<T extends Record<string, unknown>>(
  formData: Record<string, string | undefined | null>,
  originalData: Record<string, unknown>,
  fieldConfigs: Record<string, { required?: boolean }>
): Partial<T> {
  const updateData: Partial<T> = {};
  
  for (const [field, config] of Object.entries(fieldConfigs)) {
    const changedValue = getChangedField(
      formData[field],
      originalData[field] as string | undefined | null,
      config
    );
    
    if (changedValue !== undefined) {
      updateData[field as keyof T] = changedValue as T[keyof T];
    }
  }
  
  return updateData;
}

/**
 * Normalize a website value by auto-prepending https:// for bare domains.
 * Returns null for empty/whitespace-only input.
 * If any URI scheme is present (http://, ftp://, mailto:, etc.), the value is
 * left untouched so that downstream validation (isValidWebsite) can reject
 * schemes that are not http(s).
 */
export function normalizeWebsite(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Fix common missing-slashes typo: "http:example.com" or "http:/example.com" → "http://example.com"
  const missingSlashes = /^(https?):(?:\/)?(?!\/)(.+)$/i.exec(trimmed);
  if (missingSlashes) return `${missingSlashes[1].toLowerCase()}://${missingSlashes[2]}`;
  // Detect real URI schemes: either "scheme://" or known no-authority schemes like "mailto:"
  if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//i.test(trimmed)) return trimmed;
  if (/^(mailto|tel|data|javascript|blob|urn):/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Validate that a website value is a fully-qualified http(s) URL.
 * Returns true for null/empty (field is optional).
 */
export function isValidWebsite(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  // Must start with http:// or https:// (consistent with backend schema)
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return (url.protocol === 'http:' || url.protocol === 'https:') && !!url.hostname;
  } catch {
    return false;
  }
}
